import { createApi } from 'unsplash-js';
import { callOpenAIChatCompletion } from './secureProxy';

// Initialisation Unsplash avec la clé d'accès publique.
// Unsplash reste une intégration frontend acceptable si la clé est une clé publique dédiée.
const unsplash = createApi({
  accessKey: import.meta.env.VITE_UNSPLASH_ACCESS_KEY || '',
});

// Structure des données du blog
export interface BlogData {
    title: string;
    intro: string;
    sections: { title: string; content: string; photoUrl: string; photoCredit: string }[];
    conclusion: string;
    generatedAt: number;
}

// Durée de vie du cache : 24 heures
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// --- DICTIONNAIRE DE RECHERCHE INTELLIGENT (CORRIGÉ) ---
// On utilise des termes plus larges pour garantir des résultats.
const SEARCH_MAPPING: { [key: string]: string } = {
    // Cas spécifiques qui posaient problème
    "Gaspésie–Îles-de-la-Madeleine": "Gaspesie landscape", // Tiret long
    "Gaspésie-Îles-de-la-Madeleine": "Gaspesie landscape", // Tiret court (au cas où)
    "Gaspésie": "Gaspesie rock",
    "Îles-de-la-Madeleine": "Magdalen Islands",
    "Charlevoix": "Charlevoix region", // Plus large que Baie-Saint-Paul
    
    // Autres régions
    "Bas-Saint-Laurent": "Kamouraska sunset",
    "Chaudière-Appalaches": "Levis Quebec city view",
    "Outaouais": "Gatineau park nature",
    "Abitibi-Témiscamingue": "Quebec boreal forest",
    "Saguenay–Lac-Saint-Jean": "Fjord Saguenay summer",
    "Laurentides": "Mont-Tremblant village",
    "Mauricie": "Mauricie National Park canoe",
    "Lanaudière": "Quebec rawdon falls",
    "Estrie": "Eastern Townships nature",
    "Montérégie": "Mont-Saint-Hilaire view",
    "Centre-du-Québec": "Drummondville river",
    "Côte-Nord": "Tadoussac whale watching"
};

// --- IMAGES DE SECOURS (FALLBACK) ---
// Si vraiment Unsplash ne trouve rien, on affiche ces belles images génériques
const FALLBACK_IMAGES = [
    'https://images.unsplash.com/photo-1518182170546-0766aa6f6a56?auto=format&fit=crop&w=1000&q=80', // Hiver / Neige
    'https://images.unsplash.com/photo-1589828156712-b5e09f5db7c9?auto=format&fit=crop&w=1000&q=80', // Nature Verte
    'https://images.unsplash.com/photo-1499696010180-ea84df2253db?auto=format&fit=crop&w=1000&q=80', // Forêt Sapins
    'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1000&q=80'  // Montagne / Lac
];

const buildFallbackTextContent = (regionName: string, season: string) => ({
    title: `${regionName} en mode ${season}`,
    intro: `${regionName} dévoile une facette unique du Québec, entre paysages distinctifs, adresses locales et expériences à vivre au rythme de la saison. Cette suggestion Forfeo vous aide à découvrir la région avec un regard pratique, inspirant et orienté terrain.`,
    sections: [
        {
            title: `Découvrir l'identité de ${regionName}`,
            content: `Commencez par les lieux emblématiques, les points de vue et les centres-villes ou villages qui donnent le ton à la région. Prenez le temps de repérer les commerces locaux, les marchés, les attraits culturels et les arrêts spontanés qui rendent l'expérience plus authentique.`,
        },
        {
            title: `Profiter de la saison ${season}`,
            content: `Adaptez votre itinéraire à la météo et aux activités saisonnières : sorties nature, haltes gourmandes, routes panoramiques, festivals ou moments de détente. Le meilleur voyage est souvent celui qui combine une bonne planification avec une marge pour les découvertes.`,
        },
        {
            title: 'Vivre l’expérience locale',
            content: `Privilégiez les entreprises locales, les expériences guidées, les restaurants de quartier et les lieux recommandés par les gens de la région. C’est souvent là que se trouvent les souvenirs les plus forts et les découvertes qui différencient vraiment un séjour.`,
        },
    ],
    conclusion: `Avec Forfeo Explore, ${regionName} devient plus qu’une destination : c’est un parcours à personnaliser selon vos envies, votre budget et votre rythme.`,
});

const safeParseJson = (raw: string) => {
    const cleaned = String(raw || '').replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(cleaned);
};

const generateTextContent = async (regionName: string, season: string) => {
    const prompt = `
        Agis comme un rédacteur de voyage professionnel pour un magazine haut de gamme type "National Geographic Traveler".
        Sujet : Écris un article inspirant, factuel et immersif sur la région de "${regionName}, Québec" en saison "${season}".
        Ton ton doit être invitant, poétique mais précis.
        
        Format de réponse attendu : JSON strict uniquement.
        Structure JSON demandée :
        {
            "title": "Titre accrocheur et inspirant (max 10 mots)",
            "intro": "Paragraphe d'introduction immersif (env. 60 mots) qui donne envie de visiter.",
            "sections": [
                { "title": "Sous-titre Point Fort 1 (Lieu ou Activité)", "content": "Paragraphe descriptif (env. 80-100 mots)." },
                { "title": "Sous-titre Point Fort 2 (Nature ou Culture)", "content": "Paragraphe descriptif (env. 80-100 mots)." },
                { "title": "Sous-titre Point Fort 3 (Gastronomie ou Détente)", "content": "Paragraphe descriptif (env. 80-100 mots)." }
            ],
            "conclusion": "Brève conclusion invitante (env. 40 mots)."
        }
    `;

    try {
        const aiData = await callOpenAIChatCompletion({
            model: 'gpt-4o-mini',
            messages: [
                { role: 'system', content: 'You are a helpful travel assistant that outputs only valid JSON.' },
                { role: 'user', content: prompt },
            ],
            temperature: 0.7,
        });

        const contentStr = aiData.choices?.[0]?.message?.content || '';
        const parsed = safeParseJson(contentStr);

        if (!parsed?.title || !Array.isArray(parsed?.sections)) {
            throw new Error('Réponse IA invalide pour le blog régional.');
        }

        return parsed;
    } catch (error) {
        console.warn('Blog IA indisponible, contenu de secours utilisé:', error);
        return buildFallbackTextContent(regionName, season);
    }
};

/**
 * Fonction principale pour récupérer ou générer l'article de blog
 */
export const getRegionBlogData = async (regionName: string, season: string): Promise<BlogData | null> => {
    // ⚠️ CHANGEMENT CLÉ : "v5" force la régénération immédiate après sécurisation des appels IA
    const cacheKey = `blog_v5_${regionName.replace(/\s/g, '')}_${season}`;

    // 1. VÉRIFICATION DU CACHE LOCAL
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
        try {
            const data: BlogData = JSON.parse(cached);
            if (Date.now() - data.generatedAt < CACHE_DURATION) {
                console.log("Blog chargé depuis le cache local (Rapide !)");
                return data;
            } else {
                localStorage.removeItem(cacheKey);
            }
        } catch (e) {
            localStorage.removeItem(cacheKey);
        }
    }

    console.log(`Génération du blog pour ${regionName} (${season})...`);

    try {
        // 2. APPEL IA SÉCURISÉ VIA PROXY POUR LE TEXTE
        const textContent = await generateTextContent(regionName, season);

        // 3. APPEL UNSPLASH POUR LES PHOTOS (LOGIQUE ROBUSTE)
        let cleanName = regionName.trim();
        let searchTerm = SEARCH_MAPPING[cleanName] || cleanName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const queryTerm = `${searchTerm} Quebec ${season === 'Hiver' ? 'winter' : 'summer'}`;
        
        console.log(`Recherche Unsplash optimisée pour: "${queryTerm}"`);

        const photoRes = await unsplash.search.getPhotos({
            query: queryTerm,
            page: 1,
            perPage: 3,
            orientation: 'landscape'
        });

        const photos = photoRes.response?.results || [];

        const getPhoto = (idx: number) => {
            if (photos[idx] && photos[idx].urls && photos[idx].urls.regular) {
                return photos[idx].urls.regular;
            }
            return FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
        };

        const getCredit = (idx: number) => {
            if (photos[idx] && photos[idx].user && photos[idx].user.name) {
                return photos[idx].user.name;
            }
            return 'Collection Forfeo';
        };

        // 4. ASSEMBLAGE FINAL
        const sections = Array.isArray(textContent.sections) ? textContent.sections : buildFallbackTextContent(regionName, season).sections;

        const finalBlogData: BlogData = {
            title: textContent.title,
            intro: textContent.intro,
            sections: sections.map((sec: any, index: number) => ({
                ...sec,
                photoUrl: getPhoto(index),
                photoCredit: getCredit(index)
            })),
            conclusion: textContent.conclusion,
            generatedAt: Date.now()
        };

        // 5. SAUVEGARDE
        localStorage.setItem(cacheKey, JSON.stringify(finalBlogData));

        return finalBlogData;

    } catch (error) {
        console.error("Erreur critique génération blog:", error);
        return null;
    }
};
